import { useState, useEffect, useCallback } from "react";
import { get } from "../../helpers/aws_api_helper";
import { getApiErrorMessage } from "../../helpers/aws_api_helper";
import * as url from "../../helpers/url_helper";

/**
 * Fetches ARN views summary scoped to a specific account.
 * Calls /cloudnova/arn-views/accounts/:account/summary
 * Returns { data: { overall, services }, loading, error, refetch }.
 */
export function useAccountSummaryData(accountNumber) {
  const [data, setData] = useState(null);
  // Start in a loading state so consumers can show skeletons/spinners
  // until an account is selected and the first summary load completes.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSummary = useCallback(async () => {
    if (!accountNumber) {
      // No account selected: expose an "idle" state instead of perpetual loading.
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await get(
        `${url.ARN_VIEWS_BASE}/accounts/${encodeURIComponent(accountNumber)}/summary`
      );
      const status = response?.status;
      if (status >= 200 && status < 300) {
        const overall = response?.overall ?? null;
        const services = Array.isArray(response?.services) ? response.services : [];
        setData({ overall, services });
      } else {
        setError(getApiErrorMessage(response) || "Failed to load summary");
        setData(null);
      }
    } catch (err) {
      const errData = err?.data ?? err?.response?.data;
      setError(getApiErrorMessage(errData) || err?.message || "Failed to load summary");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [accountNumber]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  return { data, loading, error, refetch: fetchSummary };
}

